import type { SerializedLogRecord } from "@/types/logs";
import type { RouteAnalyticsSummary } from "@/types/route-analytics";

export const EXPORT_COLUMNS = [
  "errorClassification",
  "isError",
  "statusCode",
  "userId",
  "route",
  "normalizedRoute",
  "method",
  "errorType",
  "errorMessage",
  "createdAt",
  "updatedAt",
  "responseStored",
  "id",
  "input",
  "response",
] as const;

export function csvEscapeValue(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function toCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

export function recordToExportRow(record: SerializedLogRecord): string[] {
  return [
    toCell(record.errorClassification),
    toCell(record.isError),
    toCell(record.statusCode),
    toCell(record.userId),
    toCell(record.route),
    toCell(record.normalizedRoute),
    toCell(record.method),
    toCell(record.errorType),
    toCell(record.errorMessage),
    toCell(record.createdAt),
    toCell(record.updatedAt),
    toCell(record.responseStored),
    toCell(record.id),
    toCell(record.inputRaw),
    toCell(record.responseRaw),
  ];
}

export function rowsToCsv(rows: string[][]): string {
  return rows.map((row) => row.map(csvEscapeValue).join(",")).join("\r\n");
}

export function buildLogsCsv(records: SerializedLogRecord[]): string {
  const header = [...EXPORT_COLUMNS];
  const rows = [header, ...records.map(recordToExportRow)];
  return rowsToCsv(rows);
}

export const ROUTE_ANALYTICS_EXPORT_COLUMNS = [
  "route",
  "totalRequests",
  "successCount",
  "errorCount",
  "lastActivityAt",
] as const;

export function buildRouteAnalyticsCsv(routes: RouteAnalyticsSummary[]): string {
  const header = [...ROUTE_ANALYTICS_EXPORT_COLUMNS];
  const dataRows = routes.map((row) => [
    toCell(row.route),
    toCell(row.totalRequests),
    toCell(row.successCount),
    toCell(row.errorCount),
    toCell(row.lastActivityAt),
  ]);
  return rowsToCsv([header, ...dataRows]);
}

function sanitizeFilenameSegment(segment: string): string {
  return segment.replace(/[^a-zA-Z0-9-_]/g, "-");
}

export function buildExportFilename(prefix = "dynamodb-dashboard-logs", date: Date = new Date()): string {
  const isoDate = date.toISOString().slice(0, 10);
  return `${sanitizeFilenameSegment(prefix)}-${isoDate}.csv`;
}
